import { motion } from 'framer-motion';
import { HORECA } from '@/constants/testIds';
import { ShieldCheck, ExternalLink, FileText, Download } from 'lucide-react';

const CERTS = [
  {
    slug: 'iso-9001',
    title: 'ISO 9001:2015',
    subtitle: 'Kalite Yönetim Sistemi',
    body: 'Üretim, işleme ve paketleme süreçlerinin uluslararası kalite yönetim standardına uygunluğu.',
    issuer: 'ORSER Kontrol ve Sertifikasyon A.Ş.',
    validity: '23.01.2026 — 23.01.2029',
    number: 'SZ-YS-2023-001-18',
    image: '/certificates/iso-9001.png',
    pdf: '/certificates/iso-9001.pdf',
    accent: '#3A5A3F',
  },
  {
    slug: 'organik-tarim',
    title: 'Organik Tarım',
    subtitle: 'Müteşebbis Sertifikası',
    body: 'Üretici ve işleyici olarak organik bitkisel ürünlerin (reçel, pekmez, tahin) organik tarım mevzuatına uygunluğu.',
    issuer: 'ORSER Kontrol ve Sertifikasyon A.Ş.',
    validity: '10.10.2025 — 10.10.2026',
    number: 'MSR-5503',
    image: '/certificates/organik-tarim.png',
    pdf: '/certificates/organik-tarim.pdf',
    accent: '#B08834',
  },
];

export default function Certificates() {
  return (
    <section
      id="sertifikalar"
      data-testid={HORECA.certificatesSection}
      className="relative paper-cream text-[#1F1F1F] py-24 md:py-32 overflow-hidden"
    >
      <div className="grain" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 mb-14">
          <div className="lg:col-span-7">
            <span className="eyebrow text-[#B08834] block mb-4">Belgeler &amp; Güvence</span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-[64px] leading-[1.02] tracking-[-0.01em] font-light">
              Söz değil,
              <span className="italic text-[#B03A2E]"> sertifika</span>.
            </h2>
          </div>
          <p className="lg:col-span-5 lg:pt-4 text-[#5A5145] text-base md:text-lg leading-relaxed font-body font-light">
            Her ürünümüz bağımsız denetim kuruluşu tarafından belgelenir.
            Güncel sertifikalarımızı aşağıdan görüntüleyebilir; numune talebinizle
            birlikte kurumsal dosyalarımızı paylaşabiliriz.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {CERTS.map((c, i) => (
            <motion.a
              key={c.slug}
              href={c.pdf}
              target="_blank"
              rel="noreferrer"
              data-testid={HORECA.certificateCard(c.slug)}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="group relative bg-[#FFFDF7] dark-frame block overflow-hidden flex flex-col sm:flex-row"
            >
              {/* Thumbnail */}
              <div className="relative sm:w-[42%] bg-[#F3EEE2] overflow-hidden">
                <img
                  src={c.image}
                  alt={`${c.title} sertifikası`}
                  className="w-full h-full object-cover object-top transition-transform duration-[1200ms] group-hover:scale-[1.04]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1F1F1F]/10 to-transparent" />
              </div>

              {/* Detay */}
              <div className="sm:w-[58%] p-7 md:p-9 flex flex-col">
                <div
                  className="inline-flex items-center gap-2 self-start label-pill text-[#FBF7EE] mb-5"
                  style={{ background: c.accent }}
                >
                  <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2} />
                  Belgeli
                </div>
                <h3 className="font-display text-3xl md:text-4xl leading-none text-[#1F1F1F]">
                  {c.title}
                </h3>
                <p className="eyebrow text-[#5A5145] mt-2">{c.subtitle}</p>
                <p className="text-sm md:text-base font-body font-light text-[#5A5145] leading-relaxed mt-4">
                  {c.body}
                </p>

                <dl className="mt-6 space-y-2 text-xs font-body text-[#5A5145] border-t border-[#1F1F1F]/12 pt-5">
                  <div className="flex justify-between gap-4">
                    <dt className="text-[#1F1F1F]/50">Kuruluş</dt>
                    <dd className="text-right text-[#1F1F1F]/80">{c.issuer}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-[#1F1F1F]/50">Geçerlilik</dt>
                    <dd className="text-right text-[#1F1F1F]/80">{c.validity}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-[#1F1F1F]/50">Belge No</dt>
                    <dd className="text-right text-[#1F1F1F]/80">{c.number}</dd>
                  </div>
                </dl>

                <span className="mt-6 inline-flex items-center gap-2 text-xs tracking-[0.2em] text-[#B03A2E] group-hover:text-[#8A2E22] transition-colors font-body">
                  <FileText className="w-4 h-4" strokeWidth={1.6} />
                  PDF GÖRÜNTÜLE
                  <ExternalLink className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </div>
            </motion.a>
          ))}
        </div>

        {/* B2B Belgeler — Katalog & Fiyat Listesi */}
        <div className="mt-8 lg:mt-10 grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
          <motion.a
            href="/lutfiye-katalog.pdf"
            target="_blank"
            rel="noreferrer"
            data-testid="catalog-cta"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="group relative bg-[#1F1F1F] text-[#FBF7EE] dark-frame flex flex-col overflow-hidden"
          >
            <div className="bg-[#B03A2E] text-[#FBF7EE] p-7 md:p-8">
              <span className="eyebrow text-[#FBF7EE]/70 mb-3 block">B2B Belge · Görsel</span>
              <h3 className="font-display text-3xl md:text-4xl leading-none">
                Ürün
                <span className="italic"> Kataloğu</span>
              </h3>
              <p className="text-sm font-body font-light text-[#FBF7EE]/85 mt-3 leading-relaxed">
                Tüm ürün grupları, ambalaj seçenekleri ve görselleriyle tam katalog.
              </p>
            </div>
            <div className="p-7 md:p-8 flex flex-col justify-between flex-1">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="w-5 h-5 text-[#B08834]" strokeWidth={1.6} />
                <span className="eyebrow text-[#FBF7EE]/70">PDF · ~4.3 MB</span>
              </div>
              <span className="inline-flex items-center gap-3 self-start bg-[#FBF7EE] text-[#1F1F1F] group-hover:bg-[#B08834] px-6 py-3 text-xs tracking-[0.2em] transition-colors font-body">
                <Download className="w-4 h-4" strokeWidth={1.6} />
                KATALOĞU İNDİR
                <ExternalLink className="w-3.5 h-3.5" />
              </span>
            </div>
          </motion.a>

          <motion.a
            href="/lutfiye-toptan-fiyat-listesi.pdf"
            target="_blank"
            rel="noreferrer"
            data-testid="price-list-cta"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.8, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="group relative bg-[#1F1F1F] text-[#FBF7EE] dark-frame flex flex-col overflow-hidden"
          >
            <div className="bg-[#B08834] text-[#1F1F1F] p-7 md:p-8">
              <span className="eyebrow text-[#1F1F1F]/70 mb-3 block">B2B Belge · Fiyat</span>
              <h3 className="font-display text-3xl md:text-4xl leading-none">
                Toptan
                <span className="italic"> Fiyat Listesi</span>
              </h3>
              <p className="text-sm font-body font-light text-[#1F1F1F]/80 mt-3 leading-relaxed">
                Tüm ürün grupları için güncel toptan fiyatlar, KDV hariç.
              </p>
            </div>
            <div className="p-7 md:p-8 flex flex-col justify-between flex-1">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="w-5 h-5 text-[#B08834]" strokeWidth={1.6} />
                <span className="eyebrow text-[#FBF7EE]/70">PDF · Güncel</span>
              </div>
              <span className="inline-flex items-center gap-3 self-start bg-[#FBF7EE] text-[#1F1F1F] group-hover:bg-[#B03A2E] group-hover:text-[#FBF7EE] px-6 py-3 text-xs tracking-[0.2em] transition-colors font-body">
                <Download className="w-4 h-4" strokeWidth={1.6} />
                FİYAT LİSTESİNİ İNDİR
                <ExternalLink className="w-3.5 h-3.5" />
              </span>
            </div>
          </motion.a>
        </div>
      </div>
    </section>
  );
}
